import { Button, Notification, Spinner, toast } from 'components/ui'
import React, { useContext, useEffect, useState } from 'react'
import ParticleBackground from 'react-particle-backgrounds'
import { particleBackgroundSettingsButton } from 'configs/particle-background.config';
import { BsStars } from 'react-icons/bs';
import { TbApi, TbShieldCheck, TbRocket, TbCode, TbDatabase, TbClock } from 'react-icons/tb';
import { HiOutlineKey, HiOutlineShieldCheck, HiOutlineGlobeAlt, HiOutlineCloudArrowUp, HiOutlineDocumentText, HiOutlineLightBulb } from 'react-icons/hi2';
import CardDialog from 'views/subscription/CardDialog';
import { api as Api } from 'api/http';
import { formatNumber } from 'utils/formatNumber';
import { Trans } from 'react-i18next';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { SessionContext } from 'contexts/SessionContext';
import { ModalContext } from 'contexts/ModalContext'

export default function ApiKeysModal() {
  
  const stripe = useStripe();
  const elements = useElements();

  const { user, updateUserData } = useContext(SessionContext);
  const { closeModal } = useContext(ModalContext);

  const [ cardOpened, setCardOpened ] = useState(false);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ billingFrequency, setBillingFrequency ] = useState(user?.subscription?.items?.[0]?.price?.recurring || 'month')
  const [ item, setItem ] = useState();
  const [ subscriptionCreated, setSubscriptionCreated ] = useState(false);

  const onSubmit = async (event, withPaymentMethod = true) => {
    event && event?.preventDefault();
    setIsSubmitting(true);

    let paymentMethod;
    if (withPaymentMethod) {
      const {error, paymentMethod: _paymentMethod} = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });
      if (error) {
        setIsSubmitting(false);
        toast.push(<Notification title={error.message} type="warning" />, { placement: 'top-center' });
        return;
      }
      paymentMethod = _paymentMethod;
    }

    let newProductPriceId = item?.prices?.filter(e => e.recurring == billingFrequency)?.[0]?.id;
    let newSubscription = user?.subscription?.items?.map((item) => {
      return {
        "id": item.price.id,
        "quantity": item.quantity
      }
    });

    if (newSubscription?.filter(item => item.id == newProductPriceId)?.length == 0) {
      newSubscription.push({
        "id": newProductPriceId,
        "quantity": 1
      });
    } else {
      newSubscription = newSubscription.map((item) => {
        if (item.id == newProductPriceId) {
          item.quantity = 1;
        }
        return item;
      })
    }
    
    setIsSubmitting(true);
    Api.put(`/subscriptions`, {}, { prices: newSubscription, payment_method_id: paymentMethod?.id || undefined, recurring: billingFrequency }).then((response) => {
      updateUserData();
      setIsSubmitting(false);
      closeModal();
      toast.push(<Notification title="API Keys add-on has been successfully activated!" type="success" />, { placement: 'top-center' });
    }).catch((err) => {
      toast.push(<Notification title="An error occurred. Please, try again later." type="danger" />, { placement: "top-center" });
    }).finally(() => {
      setIsSubmitting(false);
    });
  }

  const handleOnClick = () => {
    if (!!user?.subscription?.id && !!user?.pm_last_four) {
      onSubmit(undefined, false);
    } else {
      setCardOpened(true);
    }
  }

  const handleOnCloseCardDialog = () => {
    setCardOpened(false);
    if (subscriptionCreated) {
      closeModal();
    }
  }
  
  useEffect(() => {
    setIsLoading(true);
    Api.get(`/products?include=prices&filter[name]=api-keys`).then(({ data: products }) => {
      setItem(products?.[0]);
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);

  return (<>
    {!cardOpened
      ? <div className='relative'>
        <div className='bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 rounded-2xl overflow-hidden relative max-h-[90svh] overflow-y-auto px-4 py-6'>
          <ParticleBackground settings={particleBackgroundSettingsButton} className="absolute pointer-events-none" />
          <div className='w-full h-full flex flex-col gap-6 lg:gap-8 items-center justify-center p-4 lg:p-6 py-6 lg:py-8 relative z-[1]'>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <HiOutlineKey className='text-white text-2xl' />
                </div>
              </div>
            </div>
            
            <h3 className='text-white text-center text-lg lg:text-xl font-black max-w-lg'>
              Unlock programmatic access to Followr's AI platform
            </h3>
            <p className='text-white/80 text-center text-sm -mt-2 lg:-mt-4 font-medium max-w-md'>
              Integrate AI capabilities directly into your applications with secure API access.
            </p>
            
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 max-w-2xl w-full'>
              <div className='flex items-center gap-3 px-3 py-2 bg-white/10 rounded-lg backdrop-blur-sm'>
                <TbApi className='text-lg text-white flex-shrink-0' />
                <span className='text-white text-sm font-medium'>RESTful API Access</span>
              </div>
              
              <div className='flex items-center gap-3 px-3 py-2 bg-white/10 rounded-lg backdrop-blur-sm'>
                <HiOutlineShieldCheck className='text-lg text-white flex-shrink-0' />
                <span className='text-white text-sm font-medium'>Secure Authentication</span>
              </div>
              
              <div className='flex items-center gap-3 px-3 py-2 bg-white/10 rounded-lg backdrop-blur-sm'>
                <HiOutlineDocumentText className='text-lg text-white flex-shrink-0' />
                <span className='text-white text-sm font-medium'>Complete Documentation</span>
              </div>
              
              <div className='flex items-center gap-3 px-3 py-2 bg-white/10 rounded-lg backdrop-blur-sm'>
                <TbCode className='text-lg text-white flex-shrink-0' />
                <span className='text-white text-sm font-medium'>Multiple Language SDKs</span>
              </div>
            </div>

            <div className='flex flex-col gap-0 items-center mt-4'>
              <p className='text-white/80 font-semibold text-sm'>Add-on Price</p>
              <h1 className='mega-title !text-5xl text-white'>
                {isLoading
                  ? <Spinner size="48" />
                  : <>${formatNumber((item?.prices?.filter(e => e.recurring == billingFrequency)[0])?.amount / 100, 2)}</>
                }
              </h1>
              <p className='text-white/60 text-xs'>
                per {billingFrequency === 'month' ? 'month' : 'year'}
              </p>
              
              {!user?.subscription?.items?.[0]?.price?.recurring ? (
                <div className='flex gap-2 mt-4'>
                  <div className={`transition-all cursor-pointer flex items-center justify-between text-white gap-2 ${billingFrequency == `month` && `p-1 px-2.5 bg-white/20 font-semibold rounded-full backdrop-blur-sm`}`} >
                    <span onClick={() => setBillingFrequency(`month`)}>
                      <Trans i18nKey={`subscription.monthly`}>Monthly</Trans>
                    </span>
                  </div>
                  <div className={`transition-all cursor-pointer flex items-center justify-between text-white gap-2 ${billingFrequency == `year` && `p-1 px-2.5 bg-white/20 font-semibold rounded-full backdrop-blur-sm`}`} >
                    <span className='flex items-center gap-2 w-full' onClick={() => setBillingFrequency(`year`)}>
                      <span><Trans i18nKey={`subscription.annualy`}>Annually</Trans></span> 
                      <Button variant="solid" className='-mr-1 font-bold rounded-lg !p-1 !px-2.5 inline-block !h-auto !text-xs flex gap-1.5 items-center !bg-white !text-primary-600 w-auto'>
                        <TbRocket />
                        <span>Save 20%</span>
                      </Button>
                    </span>
                  </div>
                </div>
              ) : user?.subscription?.items?.[0]?.price?.recurring == 'month' ? (
                <div className={`mt-4 transition-all cursor-pointer flex items-center justify-between text-white gap-2 ${billingFrequency == `month` && `p-1 px-2.5 bg-white/20 font-semibold rounded-full backdrop-blur-sm`}`} >
                  <span onClick={() => setBillingFrequency(`month`)}>
                    <Trans i18nKey={`subscription.monthly`}>Monthly</Trans>
                  </span>
                </div>
              ) : (
                <div className={`mt-4 transition-all cursor-pointer flex items-center justify-between text-white gap-2 ${billingFrequency == `year` && `p-1 px-2.5 bg-white/20 font-semibold rounded-full backdrop-blur-sm`}`} >
                  <span className='flex items-center gap-2 w-full' onClick={() => setBillingFrequency(`year`)}>
                    <span><Trans i18nKey={`subscription.annualy`}>Annually</Trans></span> 
                    <Button variant="solid" className='-mr-1 font-bold rounded-lg !p-1 !px-2.5 inline-block !h-auto !text-xs flex gap-1.5 items-center !bg-white !text-primary-600 w-auto'>
                      <TbRocket />
                      <span>Save 20%</span>
                    </Button>
                  </span>
                </div>
              )}
            </div>

            <div className='flex justify-center gap-2 mt-4'>
              <Button 
                variant="solid"
                icon={<HiOutlineKey />}
                onClick={handleOnClick}
                className="!bg-white !text-white hover:!bg-gray-50 !border-white shadow-lg hover:shadow-xl transition-all font-bold px-8 py-3 rounded-xl"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Activate API Keys
              </Button>
            </div>
          </div>
        </div>
      </div>
      : <CardDialog 
        isOnlyContent={false}
        cardDialogOpened={cardOpened}
        setCardDialogOpened={() => handleOnCloseCardDialog()}
        handleSubmitStripe={onSubmit}
        isSubmittingCard={isSubmitting}
        subscriptionCreated={subscriptionCreated}
        isWhitelabel={false}
      />
    }
  </>)
} 